/**
 * KB-META
 * Purpose: Manage gated access to Genus Media menu items via PIN unlock.
 * Role: ui.service
 * Owner: Layout team; LastReviewed: 2025-11-13
 */
import { Injectable, Signal, computed, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';

export type GenusMediaAccessLevel = 'none' | 'creator' | 'full';

@Injectable({
  providedIn: 'root'
})
export class GenusMediaAccessService {
  private static readonly STORAGE_KEY = 'genusMediaAccessLevel';

  private readonly storedLevel = this.readLevelFromStorage();
  private readonly levelSignal = signal<GenusMediaAccessLevel>(this.storedLevel);
  private registeredMenu: MenuItem[] | null = null;

  readonly accessLevel: Signal<GenusMediaAccessLevel> = computed(() => this.levelSignal());

  registerMenuModel(menu: MenuItem[]): void {
    this.registeredMenu = menu;
    this.applyVisibility();
  }

  hasUnlockedAccess(): boolean {
    return this.levelSignal() !== 'none';
  }

  currentLevel(): GenusMediaAccessLevel {
    return this.levelSignal();
  }

  tryUnlock(pin: string): GenusMediaAccessLevel | null {
    const normalized = (pin || '').trim();
    let level: GenusMediaAccessLevel | null = null;

    if (normalized === '0001') {
      level = 'full';
    } else if (normalized === '1111') {
      level = 'creator';
    }

    if (level) {
      this.persistLevel(level);
    }

    return level;
  }

  resetAccess(): void {
    this.persistLevel('none');
  }

  private persistLevel(level: GenusMediaAccessLevel): void {
    this.levelSignal.set(level);
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(GenusMediaAccessService.STORAGE_KEY, level);
      }
    } catch {
      // Ignore storage errors (private browsing, etc.)
    }
    this.applyVisibility();
  }

  private readLevelFromStorage(): GenusMediaAccessLevel {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const stored = window.sessionStorage.getItem(GenusMediaAccessService.STORAGE_KEY) as GenusMediaAccessLevel | null;
        if (stored === 'creator' || stored === 'full') {
          return stored;
        }
      }
    } catch {
      // Ignore read errors
    }
    return 'none';
  }

  private applyVisibility(): void {
    if (!this.registeredMenu?.length) {
      return;
    }
    const genusMediaBucket = this.registeredMenu.find((item) => String(item.label || '').toUpperCase() === 'GENUSMEDIA');
    if (!genusMediaBucket?.items) {
      return;
    }

    const level = this.levelSignal();
    genusMediaBucket.items.forEach((child) => {
      const label = String(child.label || '').toLowerCase();
      if (level === 'full') {
        child.visible = true;
      } else if (level === 'creator') {
        child.visible = label.includes('creator');
      } else {
        child.visible = false;
      }
    });
  }
}


